import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form, Input } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useIwEditingStore } from '@/entities/interwills';
import { COLORS } from '@/shared/constants/colors';
import { getPersonFullName } from '@/shared/helpers/names';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddMessageModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

type FormParams = {
  text: string;
};

const prefilledText =
  'Dear my beloved Family!\r\nPlease read these words not just as part of a legal document, but as a message from my heart. I have lived a full life and now, with clarity and peace, I leave behind the fruits of my work and love.\r\nI ask you all—my family, my chosen—please respect my final wishes as expressed in this Will. More importantly, cherish one another. Let there be no conflict, no jealousy, no division caused by what I leave behind. Relationships are far more precious than any asset.\r\nSupport each other. Protect each other. Celebrate life together and keep the bonds of family strong. Let this Will not divide, but unite you.\r\nWith all my love and trust,';

export const AddMessageModal: FC<AddMessageModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addMessage' });

  const { testator, introductionMessage, updateIntroductionMessage } = useIwEditingStore();
  const modals = useModalsStore();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<FormParams>({
    defaultValues: {
      text: introductionMessage || prefilledText,
    },
  });

  const onSubmit = (values: FormParams) => {
    updateIntroductionMessage(values.text);
    modals.closeModal();
  };

  const fullname = useMemo(() => getPersonFullName(testator), [testator]);

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <Controller
          name={'text'}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <FieldContainerIw label={t(`label`)}>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input.TextArea
                    maxLength={1500}
                    rows={11}
                    placeholder={t(`placeholder`)}
                    {...field}
                  />
                  <CharCount>{field.value?.length ?? 0}/1500</CharCount>
                </Form.Item>
              </FieldContainerIw>
            );
          }}
        />
        <FullnameWrapper>
          <span>{t('fullname')}</span>
          {fullname ? <b>{fullname}</b> : <Placeholder>[Testator full name]</Placeholder>}
        </FullnameWrapper>
        <ButtonStyled loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
          {t('btn')}
        </ButtonStyled>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const CharCount = styled.div`
  font-size: 12px;
  color: ${COLORS.systemDark200};
  text-align: right;
`;

const FullnameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 24px 0 32px;
`;

const Placeholder = styled.span`
  opacity: 0.5;
`;

const ButtonStyled = styled(ButtonIw)`
  width: fit-content;
  align-self: flex-end;
`;
